// Liquidación de Compra (SRI codDoc 03). Financiero → Compras y Pagos.
// Es el comprobante que emite el COMPRADOR cuando el vendedor no puede emitir
// factura. Se firma y autoriza como cualquier comprobante electrónico, pero al
// autorizarse nace la cuenta por pagar y su asiento de compra (trigger de BD).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, FileText, Download, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { useTabsStore } from '@/stores/tabs.store';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);
const n2 = (v: number) => (Number.isFinite(v) ? v : 0);

const TARIFAS = [0, 5, 12, 14, 15];
// Tabla 6 del SRI. Se deduce de la identificación, pero el exterior hay que elegirlo.
const TIPOS_ID = [
  { v: '', t: 'Automático (según la identificación)' },
  { v: '04', t: 'RUC' },
  { v: '05', t: 'Cédula' },
  { v: '06', t: 'Pasaporte' },
  { v: '08', t: 'Identificación del exterior' },
];

const ESTADO: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado: { label: 'Encolado', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  procesando: { label: 'Procesando', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto: { label: 'Devuelto', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface Linea {
  key: number; codigo: string; descripcion: string;
  cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number;
}
interface LiqRow {
  id: string; fecha_emision: string; fecha_vencimiento: string | null;
  ruc_proveedor: string; razon_social_proveedor: string; categoria: string | null;
  subtotal: number; iva: number; importe_total: number; lineas: number;
  estado_emision: string; serie_comprobante: string | null; mensaje_sri: string | null;
  xml_url: string | null; ride_url: string | null; ticket_buy_id: string | null;
}

let _k = 1;
const nuevaLinea = (): Linea => ({
  key: _k++, codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento: 0, iva_tarifa: 0,
});
const baseLinea = (l: Linea) =>
  Math.max(n2(l.cantidad) * n2(l.precio_unitario) - n2(l.descuento), 0);
const ivaLinea = (l: Linea) => Math.round(baseLinea(l) * n2(l.iva_tarifa)) / 100;

/** El aviso fiscal que evita el error caro: cuándo se puede y qué obliga. */
function AvisoFiscal() {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/20">
      <p className="flex items-start gap-2 font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        La liquidación de compra solo procede en casos concretos
      </p>
      <ul className="mt-2 ml-6 list-disc space-y-1 text-amber-900/90 dark:text-amber-200/90">
        <li>Proveedor persona natural no obligado a llevar contabilidad e imposibilitado de emitir comprobante.</li>
        <li>Servicios prestados por extranjeros no residentes en el país.</li>
        <li>Bienes o servicios de quien no tiene RUC, dentro de los límites que fija el reglamento.</li>
      </ul>
      <p className="mt-2 ml-6 text-amber-900/90 dark:text-amber-200/90">
        Emitirla obliga a <strong>retener el 100% del IVA</strong> y la retención de renta que corresponda.
        Al autorizarse, esta compra aparece sola como sustento en Emitir Retención.
      </p>
    </div>
  );
}

export function LiquidacionCompra() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nueva' | 'lista'>('lista');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Liquidación de Compra</h1>
          <p className="text-sm text-muted-foreground">
            Emite tú el comprobante cuando el proveedor no puede hacerlo.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar' : 'Nueva'], ['lista', 'Liquidaciones']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'lista') setEditId(null); setVista(k); }}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <Nueva editId={editId} onGuardada={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['liq'] }); }}
                 onCancelar={() => { setEditId(null); setVista('lista'); }} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nueva'); }} onNueva={() => { setEditId(null); setVista('nueva'); }} />}
    </div>
  );
}

function Nueva({ editId, onGuardada, onCancelar }: { editId: string | null; onGuardada: () => void; onCancelar: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const proveedores = useMemo(
    () => partners.filter((p) => (p.tipo_partner || []).includes('proveedor') && p.identificacion), [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);
  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sri-categorias'], queryFn: async () => (await apiClient.post('/rpc/sri_categorias', {}, FIN)).data || [],
  });

  const [ident, setIdent] = useState('');
  const [razon, setRazon] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [direccion, setDireccion] = useState('');
  const [fecha, setFecha] = useState(fechaLocal());
  const [plazo, setPlazo] = useState(0);
  const [categoria, setCategoria] = useState('');
  const [centroId, setCentroId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);

  const alCambiarIdent = (v: string) => {
    setIdent(v);
    const p = proveedores.find((x) => x.identificacion === v.trim());
    if (p) setRazon(p.razon_social);
  };

  const { data: edit } = useQuery<Record<string, unknown> | null>({
    queryKey: ['liq-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/liq_obtener', { _id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    const e = edit as {
      ruc_proveedor?: string; razon_social_proveedor?: string; tipo_identificacion_proveedor?: string;
      direccion_proveedor?: string; fecha_emision?: string; plazo_dias?: number; categoria?: string;
      centro_costo_id?: string; cc_contable_id?: string; proyecto_id?: string; observaciones?: string;
      lineas?: { codigo_principal?: string; descripcion?: string; cantidad?: number; precio_unitario?: number; descuento?: number; iva_tarifa?: number }[];
    };
    setIdent(e.ruc_proveedor || '');
    setRazon(e.razon_social_proveedor || '');
    setTipoId(e.tipo_identificacion_proveedor || '');
    setDireccion(e.direccion_proveedor || '');
    setFecha(e.fecha_emision ? String(e.fecha_emision).slice(0, 10) : fechaLocal());
    setPlazo(Number(e.plazo_dias) || 0);
    setCategoria(e.categoria || '');
    setCentroId((modoCentro === 'contable' ? e.cc_contable_id : e.centro_costo_id) || '');
    setProyectoId(e.proyecto_id || '');
    setObs(e.observaciones || '');
    setLineas((e.lineas || []).map((l) => ({
      key: _k++, codigo: l.codigo_principal || '', descripcion: l.descripcion || '',
      cantidad: Number(l.cantidad) || 1, precio_unitario: Number(l.precio_unitario) || 0,
      descuento: Number(l.descuento) || 0, iva_tarifa: Number(l.iva_tarifa) || 0,
    })));
  }, [edit, modoCentro]);

  const setLinea = (key: number, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const subtotal = lineas.reduce((s, l) => s + baseLinea(l), 0);
  const descuento = lineas.reduce((s, l) => s + n2(l.descuento), 0);
  const iva = lineas.reduce((s, l) => s + ivaLinea(l), 0);
  const total = subtotal + iva;

  const guardar = useMutation({
    mutationFn: async () => {
      const cab = {
        identificacion: ident.trim(), razon_social: razon.trim(),
        tipo_identificacion: tipoId || null, direccion: direccion.trim() || null,
        fecha_emision: fecha, plazo_dias: plazo, categoria,
        centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
        proyecto_id: proyectoId || null, observaciones: obs.trim() || null,
      };
      const items = lineas.map((l) => ({
        codigo: l.codigo.trim() || null, descripcion: l.descripcion.trim(),
        cantidad: n2(l.cantidad), precio_unitario: n2(l.precio_unitario),
        descuento: n2(l.descuento), iva_tarifa: n2(l.iva_tarifa),
      }));
      return editId
        ? (await apiClient.post('/rpc/liq_actualizar', { _id: editId, _cab: cab, _items: items }, FIN)).data
        : (await apiClient.post('/rpc/liq_crear', { _cab: cab, _items: items }, FIN)).data;
    },
    onSuccess: () => { toast.success(editId ? 'Liquidación actualizada' : 'Liquidación creada'); onGuardada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puede = ident.trim() && razon.trim() && fecha && categoria && centroId
    && lineas.length > 0 && lineas.every((l) => l.descripcion.trim()) && total > 0;

  return (
    <div className="space-y-4">
      <AvisoFiscal />
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Proveedor</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Identificación *</label>
            <input className={field} list="liq-prov" value={ident} maxLength={20}
              onChange={(e) => alCambiarIdent(e.target.value)} placeholder="Cédula, RUC o ID del exterior" />
            <datalist id="liq-prov">
              {proveedores.map((p) => <option key={p.id} value={p.identificacion!}>{p.razon_social}</option>)}
            </datalist>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Nombre del proveedor *</label>
            <input className={field} value={razon} onChange={(e) => setRazon(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tipo de identificación</label>
            <select className={field} value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
              {TIPOS_ID.map((t) => <option key={t.v} value={t.v}>{t.t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Dirección</label>
            <input className={field} value={direccion} onChange={(e) => setDireccion(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha de emisión *</label>
            <input type="date" className={field} value={fecha} max={fechaLocal()}
              onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Plazo de pago (días)</label>
            <input type="number" min="0" className={field} value={plazo} onChange={(e) => setPlazo(Number(e.target.value))} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Categoría *</label>
            <select className={field} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">— Selecciona —</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{labelCentro} *</label>
            <select className={field} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {modoCentro === 'contable'
                ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            <select className={field} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">—</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Detalle</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}>
            <Plus className="w-4 h-4 mr-1" /> Línea
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <th className="py-2 pr-3 text-left w-28">Código</th>
                <th className="py-2 pr-3 text-left">Descripción</th>
                <th className="py-2 pr-3 text-right w-24">Cantidad</th>
                <th className="py-2 pr-3 text-right w-28">P. unitario</th>
                <th className="py-2 pr-3 text-right w-24">Descuento</th>
                <th className="py-2 pr-3 text-right w-24">IVA</th>
                <th className="py-2 pr-3 text-right w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3"><input className={field} value={l.codigo} onChange={(e) => setLinea(l.key, { codigo: e.target.value })} /></td>
                  <td className="py-1 pr-3"><input className={field} value={l.descripcion} onChange={(e) => setLinea(l.key, { descripcion: e.target.value })} placeholder="Bien o servicio adquirido" /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.precio_unitario} onChange={(e) => setLinea(l.key, { precio_unitario: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.descuento} onChange={(e) => setLinea(l.key, { descuento: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3">
                    <select className={`${field} text-right`} value={l.iva_tarifa} onChange={(e) => setLinea(l.key, { iva_tarifa: Number(e.target.value) })}>
                      {TARIFAS.map((t) => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{(baseLinea(l) + ivaLinea(l)).toFixed(2)}</td>
                  <td className="py-1">
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-30" disabled={lineas.length === 1}
                      onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr>
                <td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">Subtotal</td>
                <td className="py-2 pr-3 text-right tabular-nums">{subtotal.toFixed(2)}</td><td />
              </tr>
              {descuento > 0 && (
                <tr>
                  <td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">Descuento</td>
                  <td className="py-1 pr-3 text-right tabular-nums">−{descuento.toFixed(2)}</td><td />
                </tr>
              )}
              <tr>
                <td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">IVA</td>
                <td className="py-1 pr-3 text-right tabular-nums">{iva.toFixed(2)}</td><td />
              </tr>
              <tr className="text-base">
                <td colSpan={7} className="py-2 pr-3 text-right">Total</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(total)}</td><td />
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!puede} onClick={() => guardar.mutate()}>
          {editId ? 'Guardar cambios' : 'Crear liquidación'}
        </Button>
      </div>
    </div>
  );
}

function Lista({ onEdit, onNueva }: { onEdit: (id: string) => void; onNueva: () => void }) {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const { data: docs = [], isLoading } = useQuery<LiqRow[]>({
    queryKey: ['liq'],
    queryFn: async () => (await apiClient.post('/rpc/liq_listar', { _estado: 'todos' }, FIN)).data || [],
    refetchInterval: (q) => ((q.state.data || []) as LiqRow[]).some((d) => ['encolado', 'procesando'].includes(d.estado_emision)) ? 2500 : false,
  });
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['liq'] });
    void qc.invalidateQueries({ queryKey: ['cxp'] });
  };
  const emitir = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/liq_emitir', { _id: id }, FIN)).data,
    onSuccess: () => { toast.success('Liquidación encolada para emisión'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/liq_eliminar', { _id: id }, FIN)).data,
    onSuccess: () => { toast.success('Liquidación eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  // Si el trigger falló al autorizar (p. ej. período contable cerrado), esto
  // vuelve a intentar crear la cuenta por pagar sin tocar el comprobante fiscal.
  const generar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/liq_generar_compra', { _id: id }, FIN)).data,
    onSuccess: () => { toast.success('Cuenta por pagar generada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tot = docs.reduce((s, d) => s + Number(d.importe_total), 0);
  const dlg = useDialogoEnLinea();
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {dlg.ui}
      <AvisoFiscal />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Liquidaciones ({docs.length})</CardTitle>
          <Button size="sm" onClick={onNueva}><Plus className="w-4 h-4 mr-1" /> Nueva liquidación</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {docs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Todavía no hay liquidaciones de compra.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3 text-left w-10">N°</th>
                  <th className="py-2 pr-3 text-left">Fecha</th>
                  <th className="py-2 pr-3 text-left">N° Documento</th>
                  <th className="py-2 pr-3 text-left">Proveedor</th>
                  <th className="py-2 pr-3 text-left">Categoría</th>
                  <th className="py-2 pr-3 text-right">Subtotal</th>
                  <th className="py-2 pr-3 text-right">IVA</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-left">Estado</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{String(d.fecha_emision).slice(0, 10)}</td>
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || '—'}</td>
                    <td className="py-2 pr-3">
                      {d.razon_social_proveedor}
                      <span className="block text-xs text-muted-foreground">{d.ruc_proveedor}</span>
                    </td>
                    <td className="py-2 pr-3">{d.categoria || '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{Number(d.subtotal).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{Number(d.iva).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{Number(d.importe_total).toFixed(2)}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado_emision] || ESTADO.por_emitir).cls}`}>
                        {(ESTADO[d.estado_emision] || ESTADO.por_emitir).label}
                      </span>
                      {d.estado_emision === 'autorizado' && !d.ticket_buy_id && (
                        <span className="mt-1 block text-xs text-red-600">⚠ sin cuenta por pagar</span>
                      )}
                      {['devuelto', 'rechazado', 'error'].includes(d.estado_emision) && d.mensaje_sri && (
                        <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', d.mensaje_sri!)} title="Ver motivo completo"
                          className="mt-1 block max-w-xs text-left text-xs text-red-600 hover:underline">
                          ⚠ {d.mensaje_sri.length > 140 ? d.mensaje_sri.slice(0, 140) + '…' : d.mensaje_sri}
                        </button>
                      )}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {urlDoc(d.ride_url) && <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                      {urlDoc(d.xml_url) && <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                      {EMITIBLE.includes(d.estado_emision) && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>
                      )}
                      {EMITIBLE.includes(d.estado_emision) && <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>Emitir</Button>}
                      {['encolado', 'procesando'].includes(d.estado_emision) && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> procesando…</span>}
                      {d.estado_emision === 'autorizado' && !d.ticket_buy_id && (
                        <Button size="sm" variant="outline" isLoading={generar.isPending} onClick={() => generar.mutate(d.id)}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Generar CxP
                        </Button>
                      )}
                      {d.estado_emision === 'autorizado' && d.ticket_buy_id && (
                        <button className="text-xs text-primary hover:underline align-middle ml-1"
                          onClick={() => openTab({ id: '/cxp/retenciones-emitir', title: 'Emitir Retención', path: '/cxp/retenciones-emitir' })}>
                          Emitir retención
                        </button>
                      )}
                      {d.estado_emision === 'por_emitir' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Eliminar"
                          onClick={async () => { if (await dlg.confirmar('¿Eliminar esta liquidación en borrador?', { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">Total</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{usd(tot)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LiquidacionCompra;
